import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  FileCheck, Clock, CheckCircle, XCircle, AlertCircle, 
  Pen, RotateCcw, Download, History, User, Calendar,
  Shield, FileText, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ConsentTracking = ({ subjectId, onConsentSigned }) => {
  const [consents, setConsents] = useState([]);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [consentVersions, setConsentVersions] = useState([
    { id: 'v2.0', name: 'Consent Form v2.0', date: '2025-12-01', current: true },
    { id: 'v1.1', name: 'Consent Form v1.1', date: '2025-06-15', current: false },
    { id: 'v1.0', name: 'Consent Form v1.0', date: '2025-01-01', current: false },
  ]);
  const [selectedVersion, setSelectedVersion] = useState('v2.0');
  const [consentHistory, setConsentHistory] = useState([
    { action: 'Consent form sent', user: 'Dr. Smith', date: '2025-12-15 10:30 AM', status: 'completed' },
    { action: 'Reminder sent', user: 'System', date: '2025-12-16 09:00 AM', status: 'completed' },
    { action: 'Awaiting signature', user: 'Patient', date: '2025-12-17 02:15 PM', status: 'pending' },
  ]);
  
  const sigCanvas = useRef(null);

  // Mock consent list
  const pendingConsents = [
    { id: '1', patient: 'J. Smith', status: 'pending', version: 'v2.0', study: 'Diabetes T2', date: '2025-12-15' },
    { id: '2', patient: 'M. Johnson', status: 'signed', version: 'v2.0', study: 'Hypertension', date: '2025-12-14', signedDate: '2025-12-16' },
    { id: '3', patient: 'R. Williams', status: 'withdrawn', version: 'v1.1', study: 'COPD', date: '2025-12-10', withdrawnDate: '2025-12-18' },
    { id: '4', patient: 'A. Brown', status: 'pending', version: 'v2.0', study: 'Depression', date: '2025-12-17' },
  ];

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      // In real implementation, this would be sent to the backend
      console.log('Signature captured:', signatureData);
      setShowSignatureModal(false);
      // Update the consent status
      if (onConsentSigned) {
        onConsentSigned(selectedConsent, signatureData);
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'signed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Consented
          </span>
        );
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending Consent
          </span>
        );
      case 'withdrawn':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Withdrawn
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">
                  {pendingConsents.filter(c => c.status === 'pending').length}
                </p>
                <p className="text-xs text-orange-600">Pending Consent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {pendingConsents.filter(c => c.status === 'signed').length}
                </p>
                <p className="text-xs text-green-600">Consented</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">
                  {pendingConsents.filter(c => c.status === 'withdrawn').length}
                </p>
                <p className="text-xs text-red-600">Withdrawn</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Consent List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-gray-700 flex items-center">
              <FileCheck className="w-5 h-5 mr-2 text-[#1877F2]" />
              Consent Queue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingConsents.map((consent) => (
                <div 
                  key={consent.id}
                  onClick={() => setSelectedConsent(consent)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedConsent?.id === consent.id 
                      ? 'border-[#1877F2] bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{consent.patient}</p>
                        <p className="text-xs text-gray-500">{consent.study}</p>
                      </div>
                    </div>
                    {getStatusBadge(consent.status)}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Version: {consent.version}</span>
                    <span>{consent.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Consent Detail / Signature Panel */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700 flex items-center">
                <Pen className="w-5 h-5 mr-2 text-[#1877F2]" />
                E-Signature Capture
              </CardTitle>
              <select 
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(e.target.value)}
                className="text-xs border rounded px-2 py-1"
              >
                {consentVersions.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name} {v.current && '(Current)'}
                  </option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {selectedConsent ? (
              <div className="space-y-4">
                {/* Consent Preview */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="font-medium text-gray-700">Informed Consent Form</span>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1 max-h-32 overflow-y-auto">
                    <p>I, the undersigned, voluntarily agree to participate in the clinical research study conducted by CDOC Clinical Research Associates LLC.</p>
                    <p className="mt-2">I understand that:</p>
                    <ul className="list-disc list-inside ml-2">
                      <li>My participation is entirely voluntary</li>
                      <li>I may withdraw at any time without penalty</li>
                      <li>My data will be kept confidential per HIPAA regulations</li>
                    </ul>
                  </div>
                </div>

                {/* Signature Pad */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Sign Below
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white relative">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={{
                        className: 'w-full h-40 rounded-lg',
                        style: { width: '100%', height: '160px' }
                      }}
                      backgroundColor="white"
                    />
                    <div className="absolute bottom-2 left-2 right-2 border-t border-gray-300"></div>
                    <span className="absolute bottom-4 left-4 text-xs text-gray-400">Sign here</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" onClick={clearSignature}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                    <Button 
                      className="bg-[#1877F2] hover:bg-[#166FE5] flex-1" 
                      size="sm"
                      onClick={saveSignature}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Confirm Signature
                    </Button>
                  </div>
                </div>

                {/* HIPAA Notice */}
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    This signature is captured securely and stored in compliance with HIPAA regulations. 
                    A timestamped audit trail is maintained for all consent actions.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <Pen className="w-12 h-12 mb-3 opacity-50" />
                <p>Select a consent to capture signature</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consent Timeline / Audit Trail */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <History className="w-5 h-5 mr-2 text-purple-600" />
            Consent Timeline / Audit Trail
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            
            <div className="space-y-4">
              {consentHistory.map((event) => (
                <div key={`consent-${event.action}-${event.date}`} className="flex items-start gap-4 relative">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                    event.status === 'completed' 
                      ? 'bg-green-100' 
                      : 'bg-orange-100'
                  }`}>
                    {event.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Clock className="w-4 h-4 text-orange-600" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900">{event.action}</p>
                      <span className="text-xs text-gray-500">{event.date}</span>
                    </div>
                    <p className="text-sm text-gray-500">By: {event.user}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsentTracking;
